import { WriterConflictError } from '../errors.ts';
import type { StorageDriver } from '../indexeddb/driver.ts';
import { claimLease, leaseRefusalMessage, parseLease, releaseLease, type LeaseRecord, type LeaseRefusal } from './lease.ts';

export const LEASE_KEY = 'lease';

export type LeaseClock = Readonly<{ now: () => number; ttlMs: number }>;

/** A refusal carries the moment the holder's lease lapses, but only when it really will lapse:
 * a canon chronology belongs to another save and no wait can free it. */
const refusal = (decision: LeaseRefusal): WriterConflictError => new WriterConflictError(leaseRefusalMessage(decision), {
  holderId: decision.holderId,
  ...(decision.reason === 'held-by-other' ? { retryAtMs: decision.expiresAtMs } : {}),
});

/**
 * One writer per chronology. Acquisition is a single read-write transaction, so two tabs
 * racing for the same rolling save cannot both win.
 */
export class SingleWriterLock {
  readonly #driver: StorageDriver;
  readonly #chronologyId: string;
  readonly #holderId: string;
  readonly #clock: LeaseClock;
  #token = 0;

  constructor(driver: StorageDriver, chronologyId: string, holderId: string, clock: LeaseClock) {
    this.#driver = driver;
    this.#chronologyId = chronologyId;
    this.#holderId = holderId;
    this.#clock = clock;
  }

  get holderId(): string { return this.#holderId; }
  get token(): number { return this.#token; }

  async acquire(): Promise<number> {
    const outcome: { record: LeaseRecord | null } = { record: null };
    await this.#driver.write(async tx => {
      const decision = claimLease(parseLease(await tx.get('owner', LEASE_KEY)), {
        chronologyId: this.#chronologyId,
        holderId: this.#holderId,
        nowMs: this.#clock.now(),
        ttlMs: this.#clock.ttlMs,
      });
      if (!decision.granted) throw refusal(decision);
      outcome.record = decision.record;
      await tx.put('owner', LEASE_KEY, decision.record);
    });
    this.#token = outcome.record!.fencingToken;
    return this.#token;
  }

  async release(): Promise<void> {
    await this.#driver.write(async tx => {
      const current = parseLease(await tx.get('owner', LEASE_KEY));
      if (current?.fencingToken !== this.#token) return;
      const remaining = releaseLease(current, this.#holderId);
      if (remaining === null && current) await tx.put('owner', LEASE_KEY, { ...current, expiresAtMs: 0 });
      else await tx.put('owner', LEASE_KEY, remaining);
    });
  }
}
